import React, { useCallback, useLayoutEffect, useEffect, useRef, useState } from 'react';
import ForceGraph2D, { ForceGraphMethods } from 'react-force-graph-2d';

export type KeywordNetworkNode = {
  id: string;
  value: number;
};

export type KeywordNetworkLink = {
  source: string;
  target: string;
  value: number;
};

export type KeywordNetworkData = {
  nodes: KeywordNetworkNode[];
  links: KeywordNetworkLink[];
};

type Props = {
  data: KeywordNetworkData;
  onNodeClick?: (word: string) => void;
};

const KeywordNetwork: React.FC<Props> = ({ data, onNodeClick }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphRef = useRef<ForceGraphMethods<KeywordNetworkNode, KeywordNetworkLink> | undefined>(undefined);
  const [width, setWidth] = useState(1400);
  const [copied, setCopied] = useState(false);
  const graphHeight = 520;

  const measureWidth = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;

    const rectWidth = element.getBoundingClientRect().width;
    const parentWidth = element.parentElement?.clientWidth || 0;
    const sectionWidth = (element.closest('section')?.clientWidth || 0) - 48;
    const measuredWidth = Math.max(rectWidth, element.clientWidth, parentWidth, sectionWidth);
    const nextWidth = Math.max(700, Math.floor(measuredWidth || 0));
    setWidth((currentWidth) => (Math.abs(currentWidth - nextWidth) > 1 ? nextWidth : currentWidth));
  }, []);

  const fitGraph = useCallback((duration = 500) => {
    const graph = graphRef.current;
    if (!graph || data.nodes.length === 0) return;

    const padding = Math.max(140, Math.min(width, graphHeight) * 0.3);
    graph.zoomToFit(duration, padding);
  }, [data.nodes.length, width]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    measureWidth();
    const raf = window.requestAnimationFrame(measureWidth);

    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => {
          measureWidth();
        });

    observer?.observe(containerRef.current);
    window.addEventListener('resize', measureWidth);

    return () => {
      window.cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('resize', measureWidth);
    };
  }, [measureWidth]);

  useEffect(() => {
    const graph = graphRef.current;
    if (!graph || data.nodes.length === 0) return;

    graph.d3Force('charge')?.strength?.(-160);
    graph.d3Force('link')?.distance?.((link: KeywordNetworkLink) => {
      const value = typeof link.value === 'number' ? link.value : 1;
      return Math.max(70, 130 - value * 7);
    });
    graph.d3Force('collision', (alpha: number) => {
      const nodes = data.nodes as Array<KeywordNetworkNode & { x?: number; y?: number; vx?: number; vy?: number }>;

      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const left = nodes[i];
          const right = nodes[j];
          const dx = (right.x || 0) - (left.x || 0);
          const dy = (right.y || 0) - (left.y || 0);
          const distance = Math.sqrt(dx * dx + dy * dy) || 1;
          const minDistance = 34 + Math.sqrt(left.value || 1) * 3 + Math.sqrt(right.value || 1) * 3;

          if (distance < minDistance) {
            const move = ((minDistance - distance) / distance) * alpha * 0.45;
            const moveX = dx * move;
            const moveY = dy * move;

            left.vx = (left.vx || 0) - moveX;
            left.vy = (left.vy || 0) - moveY;
            right.vx = (right.vx || 0) + moveX;
            right.vy = (right.vy || 0) + moveY;
          }
        }
      }
    });

    graph.d3ReheatSimulation();

    const firstFit = window.setTimeout(() => {
      fitGraph(650);
    }, 650);
    const settledFit = window.setTimeout(() => {
      fitGraph(650);
    }, 1400);

    return () => {
      window.clearTimeout(firstFit);
      window.clearTimeout(settledFit);
    };
  }, [data, fitGraph, width]);

  if (data.nodes.length === 0) return null;

  const createNetworkBlob = (callback: (blob: Blob | null) => void) => {
    const sourceCanvas = containerRef.current?.querySelector('canvas');
    if (!sourceCanvas || sourceCanvas.width === 0 || sourceCanvas.height === 0) {
      callback(null);
      return;
    }

    const exportWidth = 1400;
    const exportHeight = Math.round((sourceCanvas.height / sourceCanvas.width) * exportWidth);
    const canvas = document.createElement('canvas');
    canvas.width = exportWidth;
    canvas.height = exportHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      callback(null);
      return;
    }

    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(callback, 'image/png');
  };

  const handleCopy = () => {
    createNetworkBlob(async (blob) => {
      if (!blob) {
        alert('コピーできる画像が見つかりませんでした。');
        return;
      }

      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      } catch (error) {
        console.error('ネットワーク画像のコピーに失敗:', error);
        alert('クリップボードへのコピーに失敗しました。');
      }
    });
  };

  const handleDownload = () => {
    createNetworkBlob((blob) => {
      if (!blob) {
        alert('保存できる画像が見つかりませんでした。');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'keyword-network.png';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <section className="bg-white shadow-sm rounded-lg p-6 min-w-0">
      <div className="flex flex-col gap-3 border-b pb-2 mb-4 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="font-bold text-gray-800">キーワードネットワーク</h2>
        <div className="flex flex-wrap items-start gap-2 lg:justify-end">
          <div className="text-left lg:text-right">
            <span className="inline-block text-xs font-semibold px-2 py-1 rounded bg-sky-100 text-sky-800">
              抽出{data.nodes.length}語 / {data.links.length}つながり
            </span>
            <p className="mt-1 text-[11px] text-gray-500">2回以上出現した語を表示</p>
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className={`px-4 py-2 rounded text-white ${
              copied ? 'bg-emerald-500' : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {copied ? (
              <span className="whitespace-nowrap">コピー済み</span>
            ) : (
              <>
                <i className="fas fa-copy mr-2"></i>コピー
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            <i className="fas fa-download mr-2"></i>保存
          </button>
        </div>
      </div>

      <div ref={containerRef} className="h-[520px] w-full overflow-hidden rounded border border-gray-100 bg-slate-50">
        <div style={{ width, height: graphHeight }}>
          <ForceGraph2D<KeywordNetworkNode, KeywordNetworkLink>
            ref={graphRef}
            graphData={data}
            width={width}
            height={graphHeight}
            nodeLabel="id"
            nodeRelSize={5}
            nodeVal={(node) => Math.max(2, node.value)}
            nodeAutoColorBy="id"
            linkColor={() => 'rgba(71, 85, 105, 0.34)'}
            linkWidth={(link) => Math.max(1, Math.min(6, link.value))}
            d3VelocityDecay={0.32}
            cooldownTicks={140}
            onEngineStop={() => fitGraph(500)}
            onNodeClick={(node) => {
              if (typeof node.id === 'string') {
                onNodeClick?.(node.id);
              }
            }}
            nodeCanvasObject={(node, ctx, globalScale) => {
              const label = String(node.id);
              const fontSize = Math.max(9, Math.min(18, 15 / globalScale));
              const radius = Math.max(4, Math.sqrt(Math.max(1, node.value)) * 3);

              ctx.beginPath();
              ctx.arc(node.x || 0, node.y || 0, radius, 0, 2 * Math.PI, false);
              ctx.fillStyle = node.color || '#2563eb';
              ctx.fill();

              ctx.font = `600 ${fontSize}px Sans-Serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';

              const textWidth = ctx.measureText(label).width;
              const backgroundHeight = fontSize + 6;
              const x = node.x || 0;
              const y = (node.y || 0) + radius + backgroundHeight / 2 + 2;

              ctx.fillStyle = 'rgba(255, 255, 255, 0.86)';
              ctx.fillRect(x - textWidth / 2 - 4, y - backgroundHeight / 2, textWidth + 8, backgroundHeight);
              ctx.fillStyle = '#111827';
              ctx.fillText(label, x, y);
            }}
            nodePointerAreaPaint={(node, color, ctx) => {
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(node.x || 0, node.y || 0, 18, 0, 2 * Math.PI, false);
              ctx.fill();
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default KeywordNetwork;
